import { useEffect, useState } from "react";
import api, { formatIDR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, TrendingUp, TrendingDown, Wallet, AlertTriangle, Users, Building2, Package, FolderKanban } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import { toast } from "sonner";
import OnboardingWizard from "@/components/OnboardingWizard";

const Metric = ({ label, value, sub, icon: Icon, tone = "primary", testid }) => (
  <div data-testid={testid} className="card-grid p-6 animate-fade-up">
    <div className="flex items-start justify-between">
      <div className="label-tag">{label}</div>
      <Icon size={18} className={tone === "danger" ? "text-[#C45A3D]" : tone === "warn" ? "text-[#D99C3B]" : "text-[#2D4739]"} />
    </div>
    <div className="mt-4 font-display text-3xl tracking-tight num">{value}</div>
    {sub && <div className="mt-1 text-xs text-forest-400">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [ai, setAi] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then(r => setStats(r.data)).catch(() => setStats({}));
  }, []);

  const runAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/insights");
      setAi(data.insights);
    } catch (e) { toast.error("Gagal memuat insight AI"); } finally { setAiBusy(false); }
  };

  if (!stats) return <div className="text-forest-400">Memuat…</div>;

  return (
    <div className="space-y-8">
      <OnboardingWizard/>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag">Overview</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Dashboard</h1>
          <p className="text-sm text-forest-400 mt-2">Ringkasan keuangan & operasi bisnis Anda.</p>
        </div>
        <Button onClick={runAI} disabled={aiBusy} data-testid="btn-ai-insights" className="rounded-sm bg-[#151A18] hover:bg-[#22362B] text-white gap-2">
          <Sparkles size={16} /> {aiBusy ? "Menganalisis…" : "Insight AI"}
        </Button>
      </div>

      {ai && (
        <div className="card-grid p-6 border-l-4 border-l-[#D99C3B]" data-testid="ai-insights-box">
          <div className="flex items-center justify-between mb-3">
            <div className="label-tag flex items-center gap-2"><Sparkles size={12}/> AI Advisor · Claude Sonnet 4.6</div>
            <button onClick={runAI} disabled={aiBusy} className="text-xs text-forest-400 hover:text-[#151A18] underline">Regenerate</button>
          </div>
          <div className="prose prose-sm max-w-none text-[#151A18]">
            {ai.split("\n").map((line, i) => {
              const t = line.trim();
              if (!t) return <div key={i} className="h-2"/>;
              if (t.startsWith("## ")) return <div key={i} className="font-display text-lg mt-4 mb-1 first:mt-0" style={{color:"#2D4739"}}>{t.slice(3)}</div>;
              if (t.startsWith("- ")) return <div key={i} className="flex gap-2 text-sm leading-relaxed ml-1"><span className="text-[#D99C3B] mt-1">▸</span><span>{t.slice(2)}</span></div>;
              return <div key={i} className="text-sm leading-relaxed">{t}</div>;
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Metric testid="stat-sales" label="Total Penjualan" value={formatIDR(stats.total_sales)} icon={TrendingUp} />
        <Metric testid="stat-purchases" label="Total Pembelian" value={formatIDR(stats.total_purchases)} icon={TrendingDown} />
        <Metric testid="stat-profit" label="Laba Kotor" value={formatIDR(stats.profit)} icon={Wallet} />
        <Metric testid="stat-unpaid" label="Piutang" value={formatIDR(stats.unpaid_sales)} icon={AlertTriangle} tone="warn" />
        <Metric testid="stat-clients" label="Klien" value={stats.clients} icon={Users} />
        <Metric testid="stat-suppliers" label="Supplier" value={stats.suppliers} icon={Building2} />
        <Metric testid="stat-low-stock" label="Stok Rendah" value={`${stats.low_stock} / ${stats.products}`} icon={Package} tone={stats.low_stock ? "danger" : "primary"} />
        <Metric testid="stat-projects" label="Project Aktif" value={stats.projects_active} sub={`${stats.tasks_open} tugas terbuka`} icon={FolderKanban} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-grid p-6 lg:col-span-2">
          <div className="label-tag mb-4">Trend 6 Bulan</div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={stats.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E8E6" />
                <XAxis dataKey="month" tick={{ fill: '#57605A', fontSize: 12 }} />
                <YAxis tick={{ fill: '#57605A', fontSize: 12 }} />
                <Tooltip contentStyle={{ background: '#FDFDFB', border: '1px solid #E6E8E6', borderRadius: 4 }} />
                <Legend />
                <Bar dataKey="sales" name="Penjualan" fill="#2D4739" />
                <Bar dataKey="purchases" name="Pembelian" fill="#D99C3B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-grid p-6">
          <div className="label-tag mb-4">Nilai Aset</div>
          <div className="font-display text-3xl num">{formatIDR(stats.total_assets_value)}</div>
          <p className="text-sm text-forest-400 mt-3">Total nilai buku aset terdaftar. Tambahkan aset baru dari menu Aset.</p>
        </div>
      </div>
    </div>
  );
}
